import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { assessmentItemExchangeApi } from '../../services/assessmentItemExchangeApi';

export const assessmentItemExchangeKeys = {
  all: ['assessmentItemExchanges'] as const,
  lists: () => [...assessmentItemExchangeKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; search?: string }) =>
    [...assessmentItemExchangeKeys.lists(), filters] as const,
  byAssessmentExchange: (assessmentExchangeId: string) =>
    [...assessmentItemExchangeKeys.all, 'byAssessmentExchange', assessmentExchangeId] as const,
  details: () => [...assessmentItemExchangeKeys.all, 'detail'] as const,
  detail: (id: string) => [...assessmentItemExchangeKeys.details(), id] as const,
};

export function useGetAssessmentItemExchange(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assessmentItemExchangeKeys.detail(id),
    queryFn: async () => {
      const response = await assessmentItemExchangeApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment item exchange'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetAssessmentItemExchanges() {
  return useQuery({
    queryKey: assessmentItemExchangeKeys.lists(),
    queryFn: async () => {
      const response = await assessmentItemExchangeApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment item exchanges'}`);
      }
      return response;
    },
  });
}

export function useGetAssessmentItemExchangesByAssessmentExchangeId(
  assessmentExchangeId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: assessmentItemExchangeKeys.byAssessmentExchange(assessmentExchangeId),
    queryFn: async () => {
      const response = await assessmentItemExchangeApi.getByAssessmentExchangeId(assessmentExchangeId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment item exchanges'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!assessmentExchangeId,
  });
}

export function useGetAssessmentItemExchangesPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery({
    queryKey: assessmentItemExchangeKeys.list({ pageNumber, pageSize, search }),
    queryFn: async () => {
      const response = await assessmentItemExchangeApi.getPagedList(pageNumber, pageSize, search);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment item exchanges'}`);
      }
      return response;
    },
  });
}
